Auth = require 'db/auth'
Dates = require 'lib/dates'
Label = require 'models/label'
LocalStorageModel = require 'view-models/internal/local-storage-model'
Util = require 'lib/util'
_ = require 'underscore'
moment = require 'moment'
l = require 'lib/localize'

ID_NONE = '000000000000000000000000'

module.exports = class CardFilter extends LocalStorageModel
  constructor: (@board, options) ->
    super(null, options)
    @waitForId @board, (idBoard) =>
      @set({ id: "boardCardFilterSettings-#{ idBoard }" })
      @fetch()
      @_resetNewCards()

    @dueMap =
      day: 1
      week: 7
      month: 28
      overdue: 0
      notdue: 0
      complete: 0
      incomplete: 0

    @modeMap =
      or: 'or'
      and: 'and'

    @newCardsWithoutIds = []

    @listenTo @, 'change:title change:idLabels change:idMembers change:due',
      @debounce @_resetNewCards

  default: ->
    { idMembers: [], idLabels: [], title: null, due: null, limitMembers: true, limitLabels: true, newCards: [], mode: 'or' }

  clear: ->
    @save(@default())

  getBoard: ->
    @board

  _resetNewCards: ->
    @newCardsWithoutIds = []
    @save('newCards', [])

  toggleQuietMode: ->
    idMe = Auth.myId()
    return if !idMe?

    # Quiet Mode is the defaults, except that idMembers is set to you only
    quietState = @default()
    quietState.idMembers = [idMe]

    isInQuietMode = true
    for key, value of quietState
      # We don't have to worry about array ordering; all the arrays we're checking are
      # empty or contain one element
      isInQuietMode &&= _.isEqual(@get(key), value)

    if isInQuietMode
      @clear()
    else
      @save(quietState)

  toggleIdLabel: (idLabel) ->
    @toggle('idLabels', idLabel)

  toggleMember: (idMember) ->
    @toggle('idMembers', idMember)

  isFiltering: ->
    _.some([!_.isEmpty(@get('idMembers')), !_.isEmpty(@get('idLabels')), @get('title')?.trim(), @get('due')?])

  satisfiesFilter: (card) ->
    if !card?
      return false

    # cards in the archive are using CardView, so they could be filtered.
    # We want to always show them.
    if card.get('closed')
      return true

    if card.id in (@get('newCards') ? []) || card in @newCardsWithoutIds
      return true

    usingAndMode = @get('mode') == 'and'

    # Check labels and members
    for group in ['idLabels', 'idMembers'] when !_.isEmpty(@get(group))
      cardValues = card.get(group)
      if _.isEmpty(cardValues)
        cardValues = [ID_NONE]
      filterValues = @get(group)
      intersection = _.intersection(filterValues, cardValues)
      if usingAndMode
        # The card has to have all of the labels and all of the members
        if intersection.length < filterValues.length
          return false
      else
        # The card has to have at least one of the labels, and one of the members
        if intersection.length == 0
          return false

    # Check the due date
    if @get('due')?
      dueFilter = @get('due')
      dueDate = card.get('due')
      dueComplete = card.get('dueComplete')
      switch dueFilter
        when 'complete'
          if !dueComplete
            return false
        when 'incomplete'
          if !dueDate || dueComplete
            return false
        when 'notdue'
          if card.get('due')?
            return false
        when 'overdue'
          now = new Date()
          if dueComplete || !dueDate? || moment(dueDate).isAfter(now)
            return false
        else
          if dueComplete || !dueDate?
            return false
          maxDate = moment().add(@dueMap[dueFilter], 'day')
          if !moment(dueDate).isBetween(moment(), maxDate)
            return false

    # Check search terms
    search = @get('title')
    if search?
      getWords = (text) ->
        _.compact text.toLowerCase().split ///[
            \s
            \x21\x22
            # \x23 is #
            \x24-\x26
            # \x27 is '
            \x28-\x2C
            # \x2D is -
            # \x2E is . (used in email, web addresses - we want to keep them as whole words)
            \x2F
            \x3A-\x3F
            # \x40 is @ (see comment on '.')
            \x5B-\x60
            \x7B-\x7F
        ]+///

      # Has to contain at least one word from our search
      searchWords = getWords(search)

      if !_.isEmpty(searchWords)
        idShortWords = if card.get('idShort')?
          getWords card.get('idShort').toString()
        else
          []
        cardWords = _.flatten([
          getWords(card.get('name') ? '')
          idShortWords
        ])

        [completeWords..., partialWord] = searchWords
        if _.isEmpty(completeWords) || _.isEmpty(_.intersection(completeWords, cardWords))
          stringStartsWithPartialWordFun = (str) -> str.substring(0, partialWord.length) == partialWord
          if !_.any(cardWords, stringStartsWithPartialWordFun)
            return false

    return true

  addNewCard: (card) ->
    @newCardsWithoutIds.push(card)

    @waitForId card, (idCard) =>
      @newCardsWithoutIds = _.without(@newCardsWithoutIds, card)
      @addToSet('newCards', idCard)

  toJSON: ({ board, expanded } = {}) ->
    json = super
    if board
      json.board = @board.toJSON()

    if expanded
      search = @get('title') ? ""
      labelRe = /// ^ #{ Util.escapeForRegex(search) } ///i

      json.labels = _.chain(@board.getLabels())
        .filter (label) ->
          labelRe.test(label.get('color')) || labelRe.test(label.get('name'))
        .sort(Label.compare)
        .map (label) =>
          _.extend label.toJSON(),
            isActive: label.id in @get('idLabels')
        .unshift
          id: ID_NONE
          name: l(['filtering', 'no labels'])
          isActive: ID_NONE in @get('idLabels')
        .value()

      json.members = @board.memberList.chain()
        .filter (member) ->
          (search == 'me' && Auth.isMe(member)) ||
          _.any(Util.getMemNameArray(member), (name) -> labelRe.test(name))
        .map (member) =>
          _.extend member.toJSON(),
            isActive: member.id in @get('idMembers')
        .value()

      json.members = Util.sortMembers(json.members, Auth.me())
      json.members.unshift
        id: ID_NONE
        fullName: l(['filtering', 'unassigned'])
        initials: '?'
        isActive: ID_NONE in @get('idMembers')

      json.dueOptions = _.keys(@dueMap).map (time) =>
        time: time
        desc: l(['due date filter', time])
        isActive: time == @get('due')

      json.modes = _.keys(@modeMap).map (mode) =>
        mode: mode
        desc: l(['filter mode', mode])
        isActive: mode == @get('mode')

    json

  toQueryString: ->
    if @isFiltering()
      filters = []

      if (title = @get('title'))
        filters.push(title)

      @get('idLabels').forEach (idLabel) =>
        if idLabel == ID_NONE
          filters.push(['label', 'none'].join(':'))
        else
          label = @board.modelCache.get('Label', idLabel)
          if label
            if (name = label.get('name'))
              filters.push(['label', encodeURIComponent(name)].join(':'))
            else
              filters.push(['label', label.get('color')].join(':'))

      @get('idMembers').forEach (idMember) =>
        if idMember == ID_NONE
          filters.push(['member', 'none'].join(':'))
        else
          member = @board.modelCache.get('Member', idMember)
          if member
            filters.push('@' + member.get('username'))

      if (due = @get('due'))
        filters.push ['due', due].join(':')

      if @get('mode') == 'and'
        filters.push ['mode', 'and'].join(':')

      queryObj =
        menu: 'filter'
        filter: filters.join(',')

      '?' + _.chain(queryObj)
      .pairs()
      .map ([key, value]) ->
        [key, value].join('=')
      .join('&')
      .value()
    else
      ""

  fromQueryString: (filter) ->
    if !filter
      return

    idMembers = []
    idLabels = []
    due = undefined
    title = undefined

    for entry in filter.split(',')
      entry = entry.replace(/^@/, 'member:')
      [type, value] = entry.split(':')

      switch type
        when 'member'
          if value == 'none'
            idMembers.push(ID_NONE)
          else
            member = @board.modelCache.findOne 'Member', (mem) ->
              mem.get('username') == value

            if member?
              idMembers.push(member.id)

        when 'label'
          if value == 'none'
            idLabels.push(ID_NONE)
          else
            label = @board.labelList.find (label) ->
              label.get('name').toLowerCase().indexOf(value.toLowerCase()) == 0 ||
              (label.get('name') == '' && label.get('color') == value)

            if label?
              idLabels.push(label.id)

        when 'due'
          if value in _.keys(@dueMap)
            due = value

        when 'mode'
          if value == 'and'
            mode = 'and'

        else
          title = entry

      @set({ idMembers, idLabels, due, title, mode })





// WEBPACK FOOTER //
// ./app/scripts/view-models/card-filter.coffee